/**
 * 이상한 방 - 꾸밈(look) 데이터
 *
 * 근거 문서: 로드맵 확정안 3.2절(꾸밈은 `--gh-*` 덮어쓰기로만 적용되고 규칙 영향 0) · 5절(어휘: 꾸밈/look)
 *
 * ## 이 파일에는 모양이 없다
 *
 * 여기 있는 것은 **id와 자리(slot)** 뿐이다. 색값도 도형도 CSS 변수도 없다. 꾸밈이 어떻게 보이는가는
 * `ui/`의 몫이고(`gameHud.css`가 시각 규칙의 원본), 경제 모듈은 "무엇을 가졌고 무엇을 끼웠는가"만 안다.
 * 색값을 여기 두면 `game/`이 화면을 알게 되고, 그 순간 이 디렉터리를 서버로 옮길 수 없다.
 *
 * 문구도 없다. `nameKey`는 i18n 키의 **이름**이고 문장은 `i18n/ko.ts`·`en.ts`에 있다(GLOSSARY).
 *
 * ## 꾸밈이 건드릴 수 없는 것
 *
 * 액센트 노랑·먹색 플레이트·서체는 꾸밈의 대상이 아니다(로드맵 3.2절). 이름표 색이 바뀌어도 그것은
 * 플레이어를 구별하는 색이지 화면의 정체를 바꾸는 색이 아니다. 그 제약은 `ui/`가 지키되, 여기서는
 * 자리(slot)를 다섯으로 닫아 "배경색 꾸밈" 같은 자리가 데이터로 생길 수 없게 한다.
 *
 * ## 꾸밈은 어디서 오는가 - 셋뿐이다(`source`)
 *
 * **기본**은 누구나 가진 것이다. 자리마다 하나 있고 봉투에서 나오지 않는다. 기본이 풀에 들어 있으면
 * 뽑는 순간 중복이라 "가치 0인 봉투 없음"을 환급으로만 지키게 되는데, 그건 규칙을 지킨 것이 아니라 회피한 것이다.
 *
 * **봉투**는 뽑아서 얻는 것이고, 이것만 수집률의 분모다.
 *
 * **완주 보상**은 봉투에서 나오는 것을 전부 모은 사람에게만 간다. 풀에도 분모에도 없다 - 분모에 있으면
 * 자기 자신이 자기 조건이 되어 100%가 오지 않고, 풀에 있으면 다 모으기 전에 뽑혀 조건이 무너진다.
 * 세 사실 모두 `validateEnvelopeTable`이 검사한다.
 */

/* --- 자리 -------------------------------------------------------------------- */

/**
 * 꾸밈이 들어가는 다섯 자리. 순서는 사물함 화면의 나열 순서다(라운드 중 보이는 것부터).
 *
 *  - marker   : 이상한 곳을 찾았을 때 찍히는 표식의 모양. SSR 등급은 움직이는 표식이다
 *  - nametag  : 여럿이 판에서 남에게 보이는 이름표 색
 *  - stamp    : 페이즈 전환 스탬프의 문구 세트("외워라!" "누가 다녀갔다!"의 다른 말)
 *  - frame    : 결과 화면의 테두리
 *  - envelope : 봉투 겉면. 봉투를 여는 연출에서만 보인다
 */
export const LOOK_SLOTS = ['marker', 'nametag', 'stamp', 'frame', 'envelope'] as const

export type LookSlot = (typeof LOOK_SLOTS)[number]

/** 꾸밈의 출처 셋. 머리말의 세 문단이 이 셋이다. */
export type LookSource = 'default' | 'envelope' | 'complete'

/* --- 데이터 ------------------------------------------------------------------ */

/**
 * 꾸밈 전부. 기본 다섯 + 봉투에서 나오는 백여든 + 완주 보상 다섯(자리마다 하나).
 *
 * 자리마다의 수는 표식 60 · 이름표 40 · 스탬프 30 · 결과 테두리 20 · 봉투 겉면 30이다. 3.2절 표의
 * 열여덟에서 열 배로 늘린 것이고(사용자 지시), **자리마다 균등하게 늘리지 않았다** - 늘릴 수 있는 폭이
 * 자리마다 다르기 때문이다. 글자 한 자로 그리는 표식과 문구인 별명은 사실상 무한하고, 결과 테두리는
 * CSS `border` 한 줄에 토큰 색 둘뿐이라 눈으로 구별되는 조합이 스무 개 남짓에서 바닥난다.
 * 여기서 더 늘리려면 그 자리가 쓸 수 있는 CSS를 먼저 늘려야 한다(`ui/cosmeticStyles.ts`).
 *
 * `as const`로 두어 `LookId`가 이 목록에서 **파생**되게 한다. id 유니온을 따로 적으면 둘이 갈린다.
 */
export const LOOKS = [
  /* 표식 모양. 기본은 핀(현장 감식의 노란 태그 핀 - 재화 '핀'과 같은 어근). */
  { id: 'markerPin', slot: 'marker', nameKey: 'look.markerPin.name', animated: false, source: 'default' },
  { id: 'markerCircle', slot: 'marker', nameKey: 'look.markerCircle.name', animated: false, source: 'envelope' },
  { id: 'markerCross', slot: 'marker', nameKey: 'look.markerCross.name', animated: false, source: 'envelope' },
  { id: 'markerTriangle', slot: 'marker', nameKey: 'look.markerTriangle.name', animated: false, source: 'envelope' },
  { id: 'markerStar', slot: 'marker', nameKey: 'look.markerStar.name', animated: false, source: 'envelope' },
  { id: 'markerSquare', slot: 'marker', nameKey: 'look.markerSquare.name', animated: false, source: 'envelope' },
  { id: 'markerBlank', slot: 'marker', nameKey: 'look.markerBlank.name', animated: false, source: 'envelope' },
  { id: 'markerNestedSquare', slot: 'marker', nameKey: 'look.markerNestedSquare.name', animated: false, source: 'envelope' },
  { id: 'markerRuled', slot: 'marker', nameKey: 'look.markerRuled.name', animated: false, source: 'envelope' },
  { id: 'markerLattice', slot: 'marker', nameKey: 'look.markerLattice.name', animated: false, source: 'envelope' },
  { id: 'markerHatch', slot: 'marker', nameKey: 'look.markerHatch.name', animated: false, source: 'envelope' },
  { id: 'markerShade', slot: 'marker', nameKey: 'look.markerShade.name', animated: false, source: 'envelope' },
  { id: 'markerDogEar', slot: 'marker', nameKey: 'look.markerDogEar.name', animated: false, source: 'envelope' },
  { id: 'markerPunched', slot: 'marker', nameKey: 'look.markerPunched.name', animated: false, source: 'envelope' },
  { id: 'markerCornerFill', slot: 'marker', nameKey: 'look.markerCornerFill.name', animated: false, source: 'envelope' },
  { id: 'markerDiamond', slot: 'marker', nameKey: 'look.markerDiamond.name', animated: false, source: 'envelope' },
  { id: 'markerNestedDiamond', slot: 'marker', nameKey: 'look.markerNestedDiamond.name', animated: false, source: 'envelope' },
  { id: 'markerBullseye', slot: 'marker', nameKey: 'look.markerBullseye.name', animated: false, source: 'envelope' },
  { id: 'markerRing', slot: 'marker', nameKey: 'look.markerRing.name', animated: false, source: 'envelope' },
  { id: 'markerHalfMoon', slot: 'marker', nameKey: 'look.markerHalfMoon.name', animated: false, source: 'envelope' },
  { id: 'markerMissingPiece', slot: 'marker', nameKey: 'look.markerMissingPiece.name', animated: false, source: 'envelope' },
  { id: 'markerWedge', slot: 'marker', nameKey: 'look.markerWedge.name', animated: false, source: 'envelope' },
  { id: 'markerCone', slot: 'marker', nameKey: 'look.markerCone.name', animated: false, source: 'envelope' },
  { id: 'markerStarSix', slot: 'marker', nameKey: 'look.markerStarSix.name', animated: false, source: 'envelope' },
  { id: 'markerStarEight', slot: 'marker', nameKey: 'look.markerStarEight.name', animated: false, source: 'envelope' },
  { id: 'markerStarSolid', slot: 'marker', nameKey: 'look.markerStarSolid.name', animated: false, source: 'envelope' },
  { id: 'markerStarTwelve', slot: 'marker', nameKey: 'look.markerStarTwelve.name', animated: false, source: 'envelope' },
  { id: 'markerPinwheel', slot: 'marker', nameKey: 'look.markerPinwheel.name', animated: false, source: 'envelope' },
  { id: 'markerStarRinged', slot: 'marker', nameKey: 'look.markerStarRinged.name', animated: false, source: 'envelope' },
  { id: 'markerStarTeardrop', slot: 'marker', nameKey: 'look.markerStarTeardrop.name', animated: false, source: 'envelope' },
  { id: 'markerStarHollow', slot: 'marker', nameKey: 'look.markerStarHollow.name', animated: false, source: 'envelope' },
  { id: 'markerStarPlump', slot: 'marker', nameKey: 'look.markerStarPlump.name', animated: false, source: 'envelope' },
  { id: 'markerStarBead', slot: 'marker', nameKey: 'look.markerStarBead.name', animated: false, source: 'envelope' },
  { id: 'markerWhirl', slot: 'marker', nameKey: 'look.markerWhirl.name', animated: false, source: 'envelope' },
  { id: 'markerBlade', slot: 'marker', nameKey: 'look.markerBlade.name', animated: false, source: 'envelope' },
  { id: 'markerFlowerSix', slot: 'marker', nameKey: 'look.markerFlowerSix.name', animated: false, source: 'envelope' },
  { id: 'markerFlower', slot: 'marker', nameKey: 'look.markerFlower.name', animated: false, source: 'envelope' },
  { id: 'markerFlowerWhite', slot: 'marker', nameKey: 'look.markerFlowerWhite.name', animated: false, source: 'envelope' },
  { id: 'markerFlowerEight', slot: 'marker', nameKey: 'look.markerFlowerEight.name', animated: false, source: 'envelope' },
  { id: 'markerSnowflake', slot: 'marker', nameKey: 'look.markerSnowflake.name', animated: false, source: 'envelope' },
  { id: 'markerFrostFlake', slot: 'marker', nameKey: 'look.markerFrostFlake.name', animated: false, source: 'envelope' },
  { id: 'markerArrowUp', slot: 'marker', nameKey: 'look.markerArrowUp.name', animated: false, source: 'envelope' },
  { id: 'markerArrowDown', slot: 'marker', nameKey: 'look.markerArrowDown.name', animated: false, source: 'envelope' },
  { id: 'markerArrowRewind', slot: 'marker', nameKey: 'look.markerArrowRewind.name', animated: false, source: 'envelope' },
  { id: 'markerArrowSwap', slot: 'marker', nameKey: 'look.markerArrowSwap.name', animated: false, source: 'envelope' },
  { id: 'markerArrowDouble', slot: 'marker', nameKey: 'look.markerArrowDouble.name', animated: false, source: 'envelope' },
  { id: 'markerInfinity', slot: 'marker', nameKey: 'look.markerInfinity.name', animated: false, source: 'envelope' },
  { id: 'markerThreeBars', slot: 'marker', nameKey: 'look.markerThreeBars.name', animated: false, source: 'envelope' },
  { id: 'markerThreeDots', slot: 'marker', nameKey: 'look.markerThreeDots.name', animated: false, source: 'envelope' },
  { id: 'markerThickPlus', slot: 'marker', nameKey: 'look.markerThickPlus.name', animated: false, source: 'envelope' },
  { id: 'markerFullBlock', slot: 'marker', nameKey: 'look.markerFullBlock.name', animated: false, source: 'envelope' },
  { id: 'markerHalfBlock', slot: 'marker', nameKey: 'look.markerHalfBlock.name', animated: false, source: 'envelope' },
  { id: 'markerShadeBlock', slot: 'marker', nameKey: 'look.markerShadeBlock.name', animated: false, source: 'envelope' },
  /* 움직이는 표식 둘. SSR 전용이다 - `animated`는 `prefers-reduced-motion`에서 정지 꼴로 그려야 한다는 신호다. */
  { id: 'markerPulse', slot: 'marker', nameKey: 'look.markerPulse.name', animated: true, source: 'envelope' },
  { id: 'markerSpin', slot: 'marker', nameKey: 'look.markerSpin.name', animated: true, source: 'envelope' },
  { id: 'markerCog', slot: 'marker', nameKey: 'look.markerCog.name', animated: true, source: 'envelope' },
  { id: 'markerWheel', slot: 'marker', nameKey: 'look.markerWheel.name', animated: true, source: 'envelope' },
  { id: 'markerCompass', slot: 'marker', nameKey: 'look.markerCompass.name', animated: true, source: 'envelope' },
  { id: 'markerRipple', slot: 'marker', nameKey: 'look.markerRipple.name', animated: true, source: 'envelope' },
  { id: 'markerBeacon', slot: 'marker', nameKey: 'look.markerBeacon.name', animated: true, source: 'envelope' },
  { id: 'markerFloat', slot: 'marker', nameKey: 'look.markerFloat.name', animated: true, source: 'envelope' },

  /* 이름표 색. 기본은 노랑(액센트와 같은 색이라 "내 것"으로 읽힌다). */
  { id: 'tagYellow', slot: 'nametag', nameKey: 'look.tagYellow.name', animated: false, source: 'default' },
  { id: 'tagMint', slot: 'nametag', nameKey: 'look.tagMint.name', animated: false, source: 'envelope' },
  { id: 'tagCoral', slot: 'nametag', nameKey: 'look.tagCoral.name', animated: false, source: 'envelope' },
  { id: 'tagSky', slot: 'nametag', nameKey: 'look.tagSky.name', animated: false, source: 'envelope' },
  { id: 'tagLilac', slot: 'nametag', nameKey: 'look.tagLilac.name', animated: false, source: 'envelope' },
  { id: 'tagRose', slot: 'nametag', nameKey: 'look.tagRose.name', animated: false, source: 'envelope' },
  { id: 'tagStrawberry', slot: 'nametag', nameKey: 'look.tagStrawberry.name', animated: false, source: 'envelope' },
  { id: 'tagCherry', slot: 'nametag', nameKey: 'look.tagCherry.name', animated: false, source: 'envelope' },
  { id: 'tagPeach', slot: 'nametag', nameKey: 'look.tagPeach.name', animated: false, source: 'envelope' },
  { id: 'tagApricot', slot: 'nametag', nameKey: 'look.tagApricot.name', animated: false, source: 'envelope' },
  { id: 'tagCopper', slot: 'nametag', nameKey: 'look.tagCopper.name', animated: false, source: 'envelope' },
  { id: 'tagIvory', slot: 'nametag', nameKey: 'look.tagIvory.name', animated: false, source: 'envelope' },
  { id: 'tagChartreuse', slot: 'nametag', nameKey: 'look.tagChartreuse.name', animated: false, source: 'envelope' },
  { id: 'tagMugwort', slot: 'nametag', nameKey: 'look.tagMugwort.name', animated: false, source: 'envelope' },
  { id: 'tagSprout', slot: 'nametag', nameKey: 'look.tagSprout.name', animated: false, source: 'envelope' },
  { id: 'tagMoss', slot: 'nametag', nameKey: 'look.tagMoss.name', animated: false, source: 'envelope' },
  { id: 'tagGrass', slot: 'nametag', nameKey: 'look.tagGrass.name', animated: false, source: 'envelope' },
  { id: 'tagPea', slot: 'nametag', nameKey: 'look.tagPea.name', animated: false, source: 'envelope' },
  { id: 'tagGreen', slot: 'nametag', nameKey: 'look.tagGreen.name', animated: false, source: 'envelope' },
  { id: 'tagEmerald', slot: 'nametag', nameKey: 'look.tagEmerald.name', animated: false, source: 'envelope' },
  { id: 'tagCeladon', slot: 'nametag', nameKey: 'look.tagCeladon.name', animated: false, source: 'envelope' },
  { id: 'tagJade', slot: 'nametag', nameKey: 'look.tagJade.name', animated: false, source: 'envelope' },
  { id: 'tagTeal', slot: 'nametag', nameKey: 'look.tagTeal.name', animated: false, source: 'envelope' },
  { id: 'tagAqua', slot: 'nametag', nameKey: 'look.tagAqua.name', animated: false, source: 'envelope' },
  { id: 'tagOcean', slot: 'nametag', nameKey: 'look.tagOcean.name', animated: false, source: 'envelope' },
  { id: 'tagAsh', slot: 'nametag', nameKey: 'look.tagAsh.name', animated: false, source: 'envelope' },
  { id: 'tagSilver', slot: 'nametag', nameKey: 'look.tagSilver.name', animated: false, source: 'envelope' },
  { id: 'tagIndigo', slot: 'nametag', nameKey: 'look.tagIndigo.name', animated: false, source: 'envelope' },
  { id: 'tagCornflower', slot: 'nametag', nameKey: 'look.tagCornflower.name', animated: false, source: 'envelope' },
  { id: 'tagNavy', slot: 'nametag', nameKey: 'look.tagNavy.name', animated: false, source: 'envelope' },
  { id: 'tagHydrangea', slot: 'nametag', nameKey: 'look.tagHydrangea.name', animated: false, source: 'envelope' },
  { id: 'tagIris', slot: 'nametag', nameKey: 'look.tagIris.name', animated: false, source: 'envelope' },
  { id: 'tagWisteria', slot: 'nametag', nameKey: 'look.tagWisteria.name', animated: false, source: 'envelope' },
  { id: 'tagViolet', slot: 'nametag', nameKey: 'look.tagViolet.name', animated: false, source: 'envelope' },
  { id: 'tagGrape', slot: 'nametag', nameKey: 'look.tagGrape.name', animated: false, source: 'envelope' },
  { id: 'tagAmethyst', slot: 'nametag', nameKey: 'look.tagAmethyst.name', animated: false, source: 'envelope' },
  { id: 'tagPlum', slot: 'nametag', nameKey: 'look.tagPlum.name', animated: false, source: 'envelope' },
  { id: 'tagPeony', slot: 'nametag', nameKey: 'look.tagPeony.name', animated: false, source: 'envelope' },
  { id: 'tagRouge', slot: 'nametag', nameKey: 'look.tagRouge.name', animated: false, source: 'envelope' },
  { id: 'tagFuchsia', slot: 'nametag', nameKey: 'look.tagFuchsia.name', animated: false, source: 'envelope' },
  { id: 'tagBlossom', slot: 'nametag', nameKey: 'look.tagBlossom.name', animated: false, source: 'envelope' },

  /* 스탬프 문구 세트. 기본은 지금 화면의 문구다(`phase.*.stamp`). */
  { id: 'stampBasic', slot: 'stamp', nameKey: 'look.stampBasic.name', animated: false, source: 'default' },
  { id: 'stampDetective', slot: 'stamp', nameKey: 'look.stampDetective.name', animated: false, source: 'envelope' },
  { id: 'stampDeadpan', slot: 'stamp', nameKey: 'look.stampDeadpan.name', animated: false, source: 'envelope' },
  { id: 'stampLoud', slot: 'stamp', nameKey: 'look.stampLoud.name', animated: false, source: 'envelope' },
  { id: 'stampRookie', slot: 'stamp', nameKey: 'look.stampRookie.name', animated: false, source: 'envelope' },
  { id: 'stampClerk', slot: 'stamp', nameKey: 'look.stampClerk.name', animated: false, source: 'envelope' },
  { id: 'stampGuard', slot: 'stamp', nameKey: 'look.stampGuard.name', animated: false, source: 'envelope' },
  { id: 'stampCourier', slot: 'stamp', nameKey: 'look.stampCourier.name', animated: false, source: 'envelope' },
  { id: 'stampTeacher', slot: 'stamp', nameKey: 'look.stampTeacher.name', animated: false, source: 'envelope' },
  { id: 'stampBoss', slot: 'stamp', nameKey: 'look.stampBoss.name', animated: false, source: 'envelope' },
  { id: 'stampReporter', slot: 'stamp', nameKey: 'look.stampReporter.name', animated: false, source: 'envelope' },
  { id: 'stampCleaner', slot: 'stamp', nameKey: 'look.stampCleaner.name', animated: false, source: 'envelope' },
  { id: 'stampLibrarian', slot: 'stamp', nameKey: 'look.stampLibrarian.name', animated: false, source: 'envelope' },
  { id: 'stampScared', slot: 'stamp', nameKey: 'look.stampScared.name', animated: false, source: 'envelope' },
  { id: 'stampPolite', slot: 'stamp', nameKey: 'look.stampPolite.name', animated: false, source: 'envelope' },
  { id: 'stampSleepy', slot: 'stamp', nameKey: 'look.stampSleepy.name', animated: false, source: 'envelope' },
  { id: 'stampHasty', slot: 'stamp', nameKey: 'look.stampHasty.name', animated: false, source: 'envelope' },
  { id: 'stampKind', slot: 'stamp', nameKey: 'look.stampKind.name', animated: false, source: 'envelope' },
  { id: 'stampBored', slot: 'stamp', nameKey: 'look.stampBored.name', animated: false, source: 'envelope' },
  { id: 'stampFlustered', slot: 'stamp', nameKey: 'look.stampFlustered.name', animated: false, source: 'envelope' },
  { id: 'stampShy', slot: 'stamp', nameKey: 'look.stampShy.name', animated: false, source: 'envelope' },
  { id: 'stampGrumpy', slot: 'stamp', nameKey: 'look.stampGrumpy.name', animated: false, source: 'envelope' },
  { id: 'stampPlayByPlay', slot: 'stamp', nameKey: 'look.stampPlayByPlay.name', animated: false, source: 'envelope' },
  { id: 'stampBreakingNews', slot: 'stamp', nameKey: 'look.stampBreakingNews.name', animated: false, source: 'envelope' },
  { id: 'stampAd', slot: 'stamp', nameKey: 'look.stampAd.name', animated: false, source: 'envelope' },
  { id: 'stampAnnouncement', slot: 'stamp', nameKey: 'look.stampAnnouncement.name', animated: false, source: 'envelope' },
  { id: 'stampPoem', slot: 'stamp', nameKey: 'look.stampPoem.name', animated: false, source: 'envelope' },
  { id: 'stampFolkTale', slot: 'stamp', nameKey: 'look.stampFolkTale.name', animated: false, source: 'envelope' },
  { id: 'stampManual', slot: 'stamp', nameKey: 'look.stampManual.name', animated: false, source: 'envelope' },
  { id: 'stampDiary', slot: 'stamp', nameKey: 'look.stampDiary.name', animated: false, source: 'envelope' },
  { id: 'stampMachine', slot: 'stamp', nameKey: 'look.stampMachine.name', animated: false, source: 'envelope' },

  /* 결과 프레임. 기본은 테두리 없음. */
  { id: 'frameNone', slot: 'frame', nameKey: 'look.frameNone.name', animated: false, source: 'default' },
  { id: 'frameTape', slot: 'frame', nameKey: 'look.frameTape.name', animated: false, source: 'envelope' },
  { id: 'frameClip', slot: 'frame', nameKey: 'look.frameClip.name', animated: false, source: 'envelope' },
  { id: 'frameLabel', slot: 'frame', nameKey: 'look.frameLabel.name', animated: false, source: 'envelope' },
  { id: 'frameSealTape', slot: 'frame', nameKey: 'look.frameSealTape.name', animated: false, source: 'envelope' },
  { id: 'frameTearLine', slot: 'frame', nameKey: 'look.frameTearLine.name', animated: false, source: 'envelope' },
  { id: 'framePushpin', slot: 'frame', nameKey: 'look.framePushpin.name', animated: false, source: 'envelope' },
  { id: 'frameManuscript', slot: 'frame', nameKey: 'look.frameManuscript.name', animated: false, source: 'envelope' },
  { id: 'frameFold', slot: 'frame', nameKey: 'look.frameFold.name', animated: false, source: 'envelope' },
  { id: 'frameGlass', slot: 'frame', nameKey: 'look.frameGlass.name', animated: false, source: 'envelope' },
  { id: 'frameIndexTab', slot: 'frame', nameKey: 'look.frameIndexTab.name', animated: false, source: 'envelope' },
  { id: 'framePencil', slot: 'frame', nameKey: 'look.framePencil.name', animated: false, source: 'envelope' },
  { id: 'frameFolder', slot: 'frame', nameKey: 'look.frameFolder.name', animated: false, source: 'envelope' },
  { id: 'frameRuler', slot: 'frame', nameKey: 'look.frameRuler.name', animated: false, source: 'envelope' },
  { id: 'frameFilm', slot: 'frame', nameKey: 'look.frameFilm.name', animated: false, source: 'envelope' },
  { id: 'frameSpine', slot: 'frame', nameKey: 'look.frameSpine.name', animated: false, source: 'envelope' },
  { id: 'frameTwine', slot: 'frame', nameKey: 'look.frameTwine.name', animated: false, source: 'envelope' },
  { id: 'frameDoorJamb', slot: 'frame', nameKey: 'look.frameDoorJamb.name', animated: false, source: 'envelope' },
  { id: 'frameWindowSill', slot: 'frame', nameKey: 'look.frameWindowSill.name', animated: false, source: 'envelope' },
  { id: 'frameGrid', slot: 'frame', nameKey: 'look.frameGrid.name', animated: false, source: 'envelope' },
  { id: 'frameMatBoard', slot: 'frame', nameKey: 'look.frameMatBoard.name', animated: false, source: 'envelope' },

  /* 봉투 겉면. 기본은 크라프트지. 금박은 SSR 전용(3.2절 표의 "금박 봉투"). */
  { id: 'envelopeKraft', slot: 'envelope', nameKey: 'look.envelopeKraft.name', animated: false, source: 'default' },
  { id: 'envelopeStriped', slot: 'envelope', nameKey: 'look.envelopeStriped.name', animated: false, source: 'envelope' },
  { id: 'envelopeStamped', slot: 'envelope', nameKey: 'look.envelopeStamped.name', animated: false, source: 'envelope' },
  { id: 'envelopeGilt', slot: 'envelope', nameKey: 'look.envelopeGilt.name', animated: false, source: 'envelope' },
  { id: 'envelopeGrid', slot: 'envelope', nameKey: 'look.envelopeGrid.name', animated: false, source: 'envelope' },
  { id: 'envelopeLattice', slot: 'envelope', nameKey: 'look.envelopeLattice.name', animated: false, source: 'envelope' },
  { id: 'envelopePinstripe', slot: 'envelope', nameKey: 'look.envelopePinstripe.name', animated: false, source: 'envelope' },
  { id: 'envelopeHatch', slot: 'envelope', nameKey: 'look.envelopeHatch.name', animated: false, source: 'envelope' },
  { id: 'envelopeDiamond', slot: 'envelope', nameKey: 'look.envelopeDiamond.name', animated: false, source: 'envelope' },
  { id: 'envelopeCheck', slot: 'envelope', nameKey: 'look.envelopeCheck.name', animated: false, source: 'envelope' },
  { id: 'envelopeRuled', slot: 'envelope', nameKey: 'look.envelopeRuled.name', animated: false, source: 'envelope' },
  { id: 'envelopeDoubleRule', slot: 'envelope', nameKey: 'look.envelopeDoubleRule.name', animated: false, source: 'envelope' },
  { id: 'envelopeRipple', slot: 'envelope', nameKey: 'look.envelopeRipple.name', animated: false, source: 'envelope' },
  { id: 'envelopeSpeckle', slot: 'envelope', nameKey: 'look.envelopeSpeckle.name', animated: false, source: 'envelope' },
  { id: 'envelopeWoodGrain', slot: 'envelope', nameKey: 'look.envelopeWoodGrain.name', animated: false, source: 'envelope' },
  { id: 'envelopeCorrugated', slot: 'envelope', nameKey: 'look.envelopeCorrugated.name', animated: false, source: 'envelope' },
  { id: 'envelopeWideBand', slot: 'envelope', nameKey: 'look.envelopeWideBand.name', animated: false, source: 'envelope' },
  { id: 'envelopeHairline', slot: 'envelope', nameKey: 'look.envelopeHairline.name', animated: false, source: 'envelope' },
  { id: 'envelopeAir', slot: 'envelope', nameKey: 'look.envelopeAir.name', animated: false, source: 'envelope' },
  { id: 'envelopeWindow', slot: 'envelope', nameKey: 'look.envelopeWindow.name', animated: false, source: 'envelope' },
  { id: 'envelopePostmark', slot: 'envelope', nameKey: 'look.envelopePostmark.name', animated: false, source: 'envelope' },
  { id: 'envelopePerf', slot: 'envelope', nameKey: 'look.envelopePerf.name', animated: false, source: 'envelope' },
  { id: 'envelopeCrease', slot: 'envelope', nameKey: 'look.envelopeCrease.name', animated: false, source: 'envelope' },
  { id: 'envelopeDoubleSeal', slot: 'envelope', nameKey: 'look.envelopeDoubleSeal.name', animated: false, source: 'envelope' },
  { id: 'envelopeSilver', slot: 'envelope', nameKey: 'look.envelopeSilver.name', animated: false, source: 'envelope' },
  { id: 'envelopeCopper', slot: 'envelope', nameKey: 'look.envelopeCopper.name', animated: false, source: 'envelope' },
  { id: 'envelopeStain', slot: 'envelope', nameKey: 'look.envelopeStain.name', animated: false, source: 'envelope' },
  { id: 'envelopeSunburst', slot: 'envelope', nameKey: 'look.envelopeSunburst.name', animated: false, source: 'envelope' },
  { id: 'envelopeDiagonal', slot: 'envelope', nameKey: 'look.envelopeDiagonal.name', animated: false, source: 'envelope' },
  { id: 'envelopeDogEar', slot: 'envelope', nameKey: 'look.envelopeDogEar.name', animated: false, source: 'envelope' },
  { id: 'envelopeRegistered', slot: 'envelope', nameKey: 'look.envelopeRegistered.name', animated: false, source: 'envelope' },

  /*
   * 완주 보상 다섯. 자리마다 하나씩이고 **봉투에서 나오지 않는다.** 다 모은 사람에게 한 번에 앉는다
   * (`inventory.settleCompletion`).
   *
   * 수집률의 분모에도 없다(`obtainableLooks`가 `envelope`만 낸다). 분모에 넣으면 이것을 얻는 조건이
   * "이것을 이미 가진 것"이 되어 100%가 영영 오지 않는다 - 자기 자신을 조건으로 삼는 보상은 보상이 아니다.
   */
  { id: 'markerGlint', slot: 'marker', nameKey: 'look.markerGlint.name', animated: true, source: 'complete' },
  { id: 'tagPink', slot: 'nametag', nameKey: 'look.tagPink.name', animated: false, source: 'complete' },
  { id: 'stampProud', slot: 'stamp', nameKey: 'look.stampProud.name', animated: false, source: 'complete' },
  { id: 'frameGallery', slot: 'frame', nameKey: 'look.frameGallery.name', animated: false, source: 'complete' },
  { id: 'envelopeWax', slot: 'envelope', nameKey: 'look.envelopeWax.name', animated: false, source: 'complete' },
] as const satisfies readonly LookShape[]

/** `LOOKS`의 한 줄이 갖춰야 하는 모양. `satisfies`로만 쓰고 `LookDef`는 아래에서 파생한다. */
type LookShape = Readonly<{
  id: string
  slot: LookSlot
  nameKey: `look.${string}.name`
  /** 움직이는 표식인가. 표식 자리에만 참일 수 있다(`validateLooks`). */
  animated: boolean
  /**
   * 어디서 오는가. 셋뿐이고 이 한 칸이 세 가지를 동시에 정한다 - 누구나 가진 것인가(`default`) ·
   * 봉투 풀에 있는가(`envelope`) · 수집률의 분모인가(`envelope`).
   *
   * 참·거짓 둘(`isDefault` + `isComplete`)로 적지 않는 이유는 둘 다 참인 줄이 표현 가능해지기 때문이다.
   * 그 줄은 "누구나 가진 완주 보상"이라 뜻이 없는데, 만들 수 있으면 언젠가 만들어진다.
   */
  source: LookSource
}>

export type LookDef = (typeof LOOKS)[number]
export type LookId = LookDef['id']

/** id → 정의. 저장값·봉투 산출물에서 온 id를 찾는 자리다. */
const LOOK_BY_ID: ReadonlyMap<string, LookDef> = new Map(LOOKS.map((look) => [look.id, look]))

/**
 * 자리마다의 기본 꾸밈. `LOOKS`에서 계산한다 - 손으로 두 번 적으면 한쪽만 고치게 된다.
 * 자리마다 정확히 하나라는 사실은 `validateLooks`가 지키고, 여기서는 첫 것을 집는다.
 */
export const DEFAULT_LOOKS: Readonly<Record<LookSlot, LookId>> = Object.fromEntries(
  LOOK_SLOTS.map((slot) => {
    const found = LOOKS.find((look) => look.slot === slot && look.source === 'default')
    if (found === undefined) throw new Error(`꾸밈 자리 '${slot}'에 기본값이 없다`)
    return [slot, found.id]
  }),
) as Readonly<Record<LookSlot, LookId>>

/* --- 조회 -------------------------------------------------------------------- */

export function isLookId(value: unknown): value is LookId {
  return typeof value === 'string' && LOOK_BY_ID.has(value)
}

export function isLookSlot(value: unknown): value is LookSlot {
  return typeof value === 'string' && (LOOK_SLOTS as readonly string[]).includes(value)
}

export function getLook(id: LookId): LookDef {
  const look = LOOK_BY_ID.get(id)
  // `LookId`는 목록에서 파생된 유니온이라 여기 도달할 수 없다. 형식 서술이다.
  if (look === undefined) throw new RangeError(`꾸밈 '${id}'가 없다`)
  return look
}

/** 봉투에서 나올 수 있는 꾸밈. 봉투 표가 이 목록을 전부 담는지 `validateEnvelopeTable`이 본다. */
export function obtainableLooks(): readonly LookDef[] {
  return LOOKS.filter((look) => look.source === 'envelope')
}

/**
 * 다 모은 사람에게만 가는 꾸밈. 봉투 풀에 **없어야** 하고(`validateEnvelopeTable`), 수집률의 분모에도 없다.
 * 지급은 `inventory.settleCompletion` 하나가 한다 - 여기는 목록일 뿐이다.
 */
export function completionLooks(): readonly LookDef[] {
  return LOOKS.filter((look) => look.source === 'complete')
}

export function looksInSlot(slot: LookSlot): readonly LookDef[] {
  return LOOKS.filter((look) => look.slot === slot)
}

/* --- 검증 -------------------------------------------------------------------- */

/** 꾸밈 표를 검사한다. 문제가 없으면 빈 배열. */
export function validateLooks(looks: readonly LookShape[] = LOOKS): readonly string[] {
  const issues: string[] = []
  const seen = new Set<string>()
  const defaults = new Map<LookSlot, number>()
  const completions = new Map<LookSlot, number>()

  for (const look of looks) {
    if (seen.has(look.id)) issues.push(`꾸밈 id '${look.id}'가 겹친다`)
    seen.add(look.id)

    if (look.nameKey !== `look.${look.id}.name`) issues.push(`'${look.id}'의 이름 키가 id와 어긋난다: ${look.nameKey}`)
    if (look.animated && look.slot !== 'marker') issues.push(`'${look.id}'는 표식이 아닌데 움직인다`)
    if (look.source === 'default') defaults.set(look.slot, (defaults.get(look.slot) ?? 0) + 1)
    if (look.source === 'complete') completions.set(look.slot, (completions.get(look.slot) ?? 0) + 1)
  }

  for (const slot of LOOK_SLOTS) {
    const count = defaults.get(slot) ?? 0
    if (count !== 1) issues.push(`꾸밈 자리 '${slot}'의 기본값이 ${String(count)}개다 - 정확히 하나여야 한다`)
    /*
     * 완주 보상도 자리마다 하나다. 둘이면 다 모은 순간 같은 자리에 둘이 앉아 하나는 영영 안 끼워지고,
     * 없으면 그 자리는 다 모아도 아무것도 달라지지 않는다 - 어느 쪽이든 화면에서는 조용한 고장이다.
     */
    const complete = completions.get(slot) ?? 0
    if (complete !== 1) issues.push(`꾸밈 자리 '${slot}'의 완주 보상이 ${String(complete)}개다 - 정확히 하나여야 한다`)
  }

  return issues
}
